// @ts-nocheck — productions tables not yet in generated supabase-types; remove after next type regeneration
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Clapperboard,
  Plus,
  Film,
  Tv,
  Zap,
  Star,
  Lock,
  ArrowRight,
  CalendarDays,
  Users,
  CheckSquare,
  Archive,
} from 'lucide-react';

// ─── Local Types ───────────────────────────────────────────────────────────────

type ProductionType = 'film' | 'series' | 'animation' | 'short' | 'pilot' | 'documentary' | 'commercial' | 'other';
type PipelineTemplate = 'animation' | 'live_action';
type ProductionPhase = 'development' | 'pre_production' | 'production' | 'post_production' | 'completed' | 'on_hold';

interface Production {
  id: string;
  publisher_id: string;
  work_id: string | null;
  title: string;
  description: string | null;
  production_type: ProductionType;
  pipeline_template: PipelineTemplate;
  phase: ProductionPhase;
  start_date: string | null;
  target_release_date: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductionMember {
  id: string;
  production_id: string;
  auth_user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Work {
  id: string;
  title: string;
}

interface CreateProductionResult {
  success: boolean;
  production_id?: string;
  error?: string;
  message?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const PHASE_META: Record<ProductionPhase, { label: string; color: string }> = {
  development:    { label: 'Development',    color: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10' },
  pre_production: { label: 'Pre-Production', color: 'text-blue-400 border-blue-400/30 bg-blue-400/10' },
  production:     { label: 'Production',     color: 'text-green-400 border-green-400/30 bg-green-400/10' },
  post_production:{ label: 'Post',           color: 'text-purple-400 border-purple-400/30 bg-purple-400/10' },
  completed:      { label: 'Completed',      color: 'text-muted-foreground border-border bg-muted/20' },
  on_hold:        { label: 'On Hold',        color: 'text-orange-400 border-orange-400/30 bg-orange-400/10' },
};

const TYPE_ICONS: Record<ProductionType, React.ElementType> = {
  film:         Film,
  series:       Tv,
  animation:    Zap,
  short:        Film,
  pilot:        Tv,
  documentary:  Film,
  commercial:   Film,
  other:        Film,
};

function phaseBadge(phase: ProductionPhase) {
  const m = PHASE_META[phase];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${m.color}`}>
      {m.label}
    </span>
  );
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function initials(name: string | null) {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

// ─── Upsell Dialog ─────────────────────────────────────────────────────────────

function StudioProUpsell({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md partner-card p-0 overflow-hidden">
        {/* Decorative film strip top */}
        <div className="h-2 w-full film-strip-bg bg-primary/20" />

        <div className="p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Clapperboard className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="kinora-eyebrow">Studio Pro</p>
              <h2 className="font-display text-2xl font-medium leading-tight mt-0.5">Unlimited Productions</h2>
            </div>
          </div>

          <p className="text-muted-foreground text-sm leading-relaxed">
            Your free plan includes one active production. Upgrade to Studio Pro to run unlimited productions simultaneously, unlock full pipeline templates, and give your crew access to every feature.
          </p>

          <ul className="space-y-2">
            {[
              'Unlimited simultaneous productions',
              'Full animation & live-action pipelines',
              'Unlimited crew members per production',
              'Priority support for your team',
            ].map(f => (
              <li key={f} className="flex items-center gap-2.5 text-sm">
                <Star className="w-3.5 h-3.5 text-primary shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex gap-3 pt-2">
            <Button className="flex-1" onClick={() => { onClose(); navigate('/billing'); }}>
              Upgrade to Studio Pro
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={onClose}>Maybe Later</Button>
          </div>
        </div>

        {/* Lock icon watermark */}
        <Lock className="absolute bottom-4 right-4 w-24 h-24 text-primary/5 pointer-events-none" />
      </div>
    </div>
  );
}

// ─── Create Dialog ─────────────────────────────────────────────────────────────

function CreateProductionDialog({
  open,
  onClose,
  publisherId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  publisherId: string;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [productionType, setProductionType] = useState<ProductionType>('film');
  const [pipelineTemplate, setPipelineTemplate] = useState<PipelineTemplate>('live_action');
  const [startDate, setStartDate] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [workId, setWorkId] = useState('none');
  const [works, setWorks] = useState<Work[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  useEffect(() => {
    if (!open) return;
    supabase
      .from('works')
      .select('id, title')
      .eq('publisher_id', publisherId)
      .neq('status', 'archived')
      .order('title')
      .then(({ data }) => { if (data) setWorks(data as Work[]); });
  }, [open, publisherId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('create_production', {
        p_title: title.trim(),
        p_description: description.trim() || undefined,
        p_production_type: productionType,
        p_pipeline_template: pipelineTemplate,
        p_work_id: workId === 'none' ? undefined : workId,
        p_start_date: startDate || undefined,
        p_target_release_date: releaseDate || undefined,
      });
      if (error) throw error;
      const result = data as CreateProductionResult;
      if (!result.success) {
        if (result.error === 'STUDIO_PLAN_LIMIT') {
          onClose();
          setShowUpsell(true);
          return;
        }
        throw new Error(result.message || result.error || 'Failed to create production');
      }
      toast.success('Production created');
      onCreated(result.production_id!);
      onClose();
      // Reset
      setTitle(''); setDescription(''); setStartDate(''); setReleaseDate(''); setWorkId('none');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create production');
    } finally {
      setSubmitting(false);
    }
  };

  if (showUpsell) return <StudioProUpsell onClose={() => setShowUpsell(false)} />;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Production</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="prod-title">Title *</Label>
            <Input id="prod-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. The Long Exposure" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prod-desc">Description</Label>
            <Textarea id="prod-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief synopsis or production notes…" rows={3} className="resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={productionType} onValueChange={v => setProductionType(v as ProductionType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(['film','series','animation','short','pilot','documentary','commercial','other'] as ProductionType[]).map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Pipeline</Label>
              <Select value={pipelineTemplate} onValueChange={v => setPipelineTemplate(v as PipelineTemplate)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="animation">Animation</SelectItem>
                  <SelectItem value="live_action">Live Action</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start Date</Label>
              <Input id="start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="release-date">Target Release</Label>
              <Input id="release-date" type="date" value={releaseDate} onChange={e => setReleaseDate(e.target.value)} />
            </div>
          </div>

          {works.length > 0 && (
            <div className="space-y-1.5">
              <Label>Linked Work (optional)</Label>
              <Select value={workId} onValueChange={setWorkId}>
                <SelectTrigger><SelectValue placeholder="No linked work" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked work</SelectItem>
                  {works.map(w => <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Production'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Production Card ────────────────────────────────────────────────────────────

function ProductionCard({
  production,
  members,
  openTaskCount,
}: {
  production: Production;
  members: ProductionMember[];
  openTaskCount: number;
}) {
  const navigate = useNavigate();
  const TypeIcon = TYPE_ICONS[production.production_type] ?? Film;

  return (
    <div
      className="partner-card p-5 cursor-pointer group"
      onClick={() => navigate(`/productions/${production.id}`)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="mt-0.5 w-7 h-7 rounded bg-primary/10 flex items-center justify-center shrink-0">
            <TypeIcon className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-sm leading-tight truncate group-hover:text-primary transition-colors">
              {production.title}
            </h3>
            <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mt-0.5">
              {production.production_type} · {production.pipeline_template === 'animation' ? 'anim' : 'live'}
            </p>
          </div>
        </div>
        {phaseBadge(production.phase)}
      </div>

      {/* Description */}
      {production.description && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
          {production.description}
        </p>
      )}

      {/* Footer row */}
      <div className="flex items-center justify-between gap-2 pt-3 border-t border-border/50">
        {/* Team avatars */}
        <div className="flex items-center gap-1">
          <div className="flex -space-x-1.5">
            {members.slice(0, 4).map(m => (
              <Avatar key={m.id} className="w-5 h-5 border border-background ring-0">
                <AvatarImage src={m.avatar_url ?? undefined} />
                <AvatarFallback className="text-[8px] bg-muted">{initials(m.display_name)}</AvatarFallback>
              </Avatar>
            ))}
            {members.length > 4 && (
              <div className="w-5 h-5 rounded-full bg-muted border border-background flex items-center justify-center">
                <span className="text-[8px] text-muted-foreground">+{members.length - 4}</span>
              </div>
            )}
          </div>
          {members.length > 0 && (
            <span className="text-[10px] text-muted-foreground ml-1 flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5" />{members.length}
            </span>
          )}
        </div>

        {/* Task count + dates */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          {openTaskCount > 0 && (
            <span className="flex items-center gap-0.5">
              <CheckSquare className="w-2.5 h-2.5" />
              {openTaskCount} open
            </span>
          )}
          {production.target_release_date && (
            <span className="flex items-center gap-0.5">
              <CalendarDays className="w-2.5 h-2.5" />
              {fmtDate(production.target_release_date)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────────────────────────

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="partner-card flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <Clapperboard className="w-8 h-8 text-primary" />
      </div>
      <h3 className="font-display text-xl font-medium mb-1">No productions yet</h3>
      <p className="text-muted-foreground text-sm mb-6 max-w-xs">
        Create your first production to start managing tasks, shots, and your crew in one place.
      </p>
      <Button onClick={onNew}>
        <Plus className="w-4 h-4 mr-1.5" />
        New Production
      </Button>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

export default function Productions() {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [productions, setProductions] = useState<Production[]>([]);
  const [members, setMembers] = useState<Record<string, ProductionMember[]>>({});
  const [taskCounts, setTaskCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const fetchProductions = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .eq('publisher_id', publisher.id)
        .eq('is_archived', showArchived)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const prods = (data ?? []) as Production[];
      setProductions(prods);

      if (prods.length === 0) return;
      const ids = prods.map(p => p.id);

      // Fetch members for all productions
      const { data: memberData } = await supabase
        .from('production_members')
        .select('id, production_id, auth_user_id, display_name, avatar_url')
        .in('production_id', ids);

      const memberMap: Record<string, ProductionMember[]> = {};
      for (const m of memberData ?? []) {
        if (!memberMap[m.production_id]) memberMap[m.production_id] = [];
        memberMap[m.production_id].push(m as ProductionMember);
      }
      setMembers(memberMap);

      // Fetch open task counts
      const { data: taskData } = await supabase
        .from('production_tasks')
        .select('production_id, status')
        .in('production_id', ids)
        .in('status', ['backlog', 'todo', 'in_progress', 'review']);

      const countMap: Record<string, number> = {};
      for (const t of taskData ?? []) {
        countMap[t.production_id] = (countMap[t.production_id] ?? 0) + 1;
      }
      setTaskCounts(countMap);
    } catch (err: any) {
      toast.error('Failed to load productions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductions();
  }, [publisher?.id, showArchived]);

  const handleCreated = (id: string) => {
    navigate(`/productions/${id}`);
  };

  const grouped = {
    active: productions.filter(p => !['completed', 'on_hold'].includes(p.phase)),
    hold: productions.filter(p => p.phase === 'on_hold'),
    completed: productions.filter(p => p.phase === 'completed'),
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium">Productions</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your film and animation productions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowArchived(v => !v)}
            className="text-muted-foreground"
          >
            <Archive className="w-4 h-4 mr-1.5" />
            {showArchived ? 'Active' : 'Archived'}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            New Production
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="partner-card h-40 animate-pulse" />
          ))}
        </div>
      ) : productions.length === 0 ? (
        <EmptyState onNew={() => setCreateOpen(true)} />
      ) : (
        <div className="space-y-8">
          {grouped.active.length > 0 && (
            <section>
              <h2 className="kinora-eyebrow mb-3">In Production</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {grouped.active.map(p => (
                  <ProductionCard
                    key={p.id}
                    production={p}
                    members={members[p.id] ?? []}
                    openTaskCount={taskCounts[p.id] ?? 0}
                  />
                ))}
              </div>
            </section>
          )}
          {grouped.hold.length > 0 && (
            <section>
              <h2 className="kinora-eyebrow mb-3">On Hold</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {grouped.hold.map(p => (
                  <ProductionCard
                    key={p.id}
                    production={p}
                    members={members[p.id] ?? []}
                    openTaskCount={taskCounts[p.id] ?? 0}
                  />
                ))}
              </div>
            </section>
          )}
          {grouped.completed.length > 0 && (
            <section>
              <h2 className="kinora-eyebrow mb-3">Completed</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {grouped.completed.map(p => (
                  <ProductionCard
                    key={p.id}
                    production={p}
                    members={members[p.id] ?? []}
                    openTaskCount={taskCounts[p.id] ?? 0}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {publisher?.id && (
        <CreateProductionDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          publisherId={publisher.id}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
